/**
 * Module-level cache for blob URLs to persist across component lifecycles.
 * This prevents re-fetching images when components unmount/remount during
 * table pagination, sorting, or filtering.
 */

interface CacheEntry {
  blobUrl: string;
  createdAt: number;
  size: number;
}

// Module-level cache - survives component unmounts
const blobCache = new Map<string, CacheEntry>();

// Maximum cache size in bytes (50MB default)
const MAX_CACHE_SIZE = 50 * 1024 * 1024;

// Maximum age for cache entries (2 hours)
const MAX_CACHE_AGE_MS = 2 * 60 * 60 * 1000;

let totalCacheSize = 0;

/**
 * Evict a specific cache entry
 */
const evictEntry = (key: string, entry: CacheEntry): void => {
  URL.revokeObjectURL(entry.blobUrl);
  totalCacheSize -= entry.size;
  blobCache.delete(key);
};

/**
 * Evict the oldest cache entry
 */
const evictOldestEntry = (): void => {
  let oldestKey: string | null = null;
  let oldestTime = Infinity;

  blobCache.forEach((entry, key) => {
    if (entry.createdAt < oldestTime) {
      oldestTime = entry.createdAt;
      oldestKey = key;
    }
  });

  if (oldestKey) {
    const entry = blobCache.get(oldestKey);
    if (entry) {
      evictEntry(oldestKey, entry);
    }
  }
};

/**
 * Get a cached blob URL for an artifact ID.
 * Returns null if not cached or expired.
 */
export const getCachedBlobUrl = (artifactId: number): string | null => {
  const cacheKey = `artifact-image-${artifactId}`;
  const entry = blobCache.get(cacheKey);

  if (!entry) return null;

  // Check if entry has expired
  if (Date.now() - entry.createdAt > MAX_CACHE_AGE_MS) {
    evictEntry(cacheKey, entry);
    return null;
  }

  return entry.blobUrl;
};

/**
 * Cache a blob URL for an artifact ID.
 * Returns the blob URL (either existing cached or newly created).
 */
export const cacheBlobUrl = (artifactId: number, blobData: Blob): string => {
  const cacheKey = `artifact-image-${artifactId}`;

  // Check if already cached
  const existing = blobCache.get(cacheKey);
  if (existing) {
    return existing.blobUrl;
  }

  // Evict old entries if needed to make room
  while (
    totalCacheSize + blobData.size > MAX_CACHE_SIZE &&
    blobCache.size > 0
  ) {
    evictOldestEntry();
  }

  // Create and cache the blob URL
  const blobUrl = URL.createObjectURL(blobData);

  blobCache.set(cacheKey, {
    blobUrl,
    createdAt: Date.now(),
    size: blobData.size,
  });

  totalCacheSize += blobData.size;

  return blobUrl;
};

/**
 * Clear all cached blob URLs (useful for testing or memory pressure)
 */
export const clearBlobCache = (): void => {
  blobCache.forEach((entry, key) => {
    evictEntry(key, entry);
  });
};

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = (): { entries: number; sizeBytes: number } => ({
  entries: blobCache.size,
  sizeBytes: totalCacheSize,
});
